import { z } from 'zod';
import { uuidSchema } from '../primitives.js';
import { IMPORT_DATASETS, IMPORT_ROW_STATUSES, IMPORT_STATUSES } from '../enums.js';

/**
 * Excel / CSV import.
 *
 * The contract is shaped around the two-stage flow: everything below describes
 * a batch that has been *staged and counted* but not necessarily written. A
 * client can therefore render the whole review screen — counts, mapping,
 * sample rows, every failure and its reason — from one response, before any
 * business record exists.
 */

export const uploadImportSchema = z.object({
  dataset: z.enum(IMPORT_DATASETS),
});
export type UploadImportInput = z.infer<typeof uploadImportSchema>;

/** `{ canonical field: workbook header }`. An absent field is unmapped. */
export const importMappingSchema = z.object({
  mapping: z.record(z.string(), z.string().trim().min(1)),
});
export type ImportMappingInput = z.infer<typeof importMappingSchema>;

export const rollbackImportSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});
export type RollbackImportInput = z.infer<typeof rollbackImportSchema>;

export const importBatchSummarySchema = z.object({
  id: uuidSchema,
  dataset: z.enum(IMPORT_DATASETS),
  status: z.enum(IMPORT_STATUSES),
  filename: z.string(),
  sheetName: z.string().nullable(),
  fileSize: z.number(),
  totalRows: z.number(),
  /** Rows that would be written if this batch were committed now. */
  readyCount: z.number(),
  importedCount: z.number(),
  duplicateCount: z.number(),
  failedCount: z.number(),
  skippedCount: z.number(),
  committedAt: z.string().nullable(),
  rolledBackAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ImportBatchSummary = z.infer<typeof importBatchSummarySchema>;

export const importRowViewSchema = z.object({
  id: uuidSchema,
  /** The row number in the workbook, so an error names what the owner sees. */
  rowNumber: z.number(),
  raw: z.record(z.string(), z.string().nullable()),
  normalized: z.record(z.string(), z.unknown()).nullable(),
  status: z.enum(IMPORT_ROW_STATUSES),
  /** Why this row failed, in plain words. Null unless it did. */
  errorMessage: z.string().nullable(),
  /** Which mapped column the failure belongs to, for highlighting. */
  errorField: z.string().nullable(),
  entityType: z.string().nullable(),
  entityId: uuidSchema.nullable(),
});
export type ImportRowView = z.infer<typeof importRowViewSchema>;

export const importFieldSchema = z.object({
  field: z.string(),
  label: z.string(),
  required: z.boolean(),
  hint: z.string().nullable(),
});
export type ImportField = z.infer<typeof importFieldSchema>;

export const importBatchDetailSchema = importBatchSummarySchema.extend({
  columnMapping: z.record(z.string(), z.string()),
  /** Headers in the file that no field claimed — the mapping UI's to-do list. */
  unmappedHeaders: z.array(z.string()),
  /** Required fields with no column. The batch cannot commit while non-empty. */
  missingRequired: z.array(z.string()),
  fields: z.array(importFieldSchema),
  headers: z.array(z.string()),
  rows: z.array(importRowViewSchema),
  /** Every failed row, uncapped. A migration is only as good as this list. */
  failures: z.array(importRowViewSchema),
});
export type ImportBatchDetail = z.infer<typeof importBatchDetailSchema>;

/**
 * Saving a mapping for reuse.
 *
 * The mechanism that keeps the importer generic rather than fitted to
 * whichever organization was onboarded first: built-in aliases are a guess at
 * ordinary business vocabulary, and a saved mapping is what *this* company
 * knows about its own spreadsheets.
 */
export const saveImportMappingSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Applied when no saved mapping matches a workbook's header shape. */
  isDefault: z.boolean().optional(),
});
export type SaveImportMappingInput = z.infer<typeof saveImportMappingSchema>;

export const savedImportMappingSchema = z.object({
  id: uuidSchema,
  dataset: z.enum(IMPORT_DATASETS),
  name: z.string(),
  mapping: z.record(z.string(), z.string()),
  isDefault: z.boolean(),
  useCount: z.number(),
  lastUsedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type SavedImportMapping = z.infer<typeof savedImportMappingSchema>;
